import { isNullOrUndefined, vyDate } from '@vy/utils';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { userMessage } from '../../../../../../utils/vy-toasts';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';

type ResetInstallmentsParams = {
  productIndex: number;
  newStartDate: Date;
  amount: number | undefined;
  getValues: UseFormGetValues<PlanInputForm>;
  setValue: UseFormSetValue<PlanInputForm>;
};

/**
 * When a commercial-terms charge's start date moves to/after the first
 * installment, collapse the breakdown to a single installment on the new date
 * and warn the user to reconfigure. Shared by the start-date and date-range pickers.
 */
export function resetInstallmentsForStartDate({
  productIndex,
  newStartDate,
  amount,
  getValues,
  setValue,
}: ResetInstallmentsParams): void {
  const scheduledInstallmentsPath =
    `fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.scheduledInstallments` as const;
  const [firstInstallment] = getValues(scheduledInstallmentsPath)?.installments ?? [];

  if (isNullOrUndefined(firstInstallment)) {
    return;
  }

  const normalizedStart = vyDate(newStartDate).startOf('day');

  if (normalizedStart.isBefore(vyDate(firstInstallment.scheduledDateStr), 'day')) {
    return;
  }

  const scheduledDateStr = normalizedStart.toISOString();

  setValue(
    scheduledInstallmentsPath,
    {
      totalAmount: amount ?? 0,
      installments: [{ amount, scheduledDateStr }],
    },
    { shouldDirty: true },
  );
  setValue(`${scheduledInstallmentsPath}.installments.0.scheduledDateStr`, scheduledDateStr, {
    shouldDirty: true,
  });
  setValue(`${scheduledInstallmentsPath}.installments.0.amount`, amount, { shouldDirty: true });
  userMessage('warning', 'Installments reset due to custom start date change. Please reconfigure.');
}
